import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
} from '@klaro7/ui';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { PhoneVerification } from '@/features/phone-verification/components/PhoneVerification';

import { useCall } from '../context/CallContext';
import { IncomingCallModal } from './IncomingCallModal';
import { ResumeSessionModal } from './ResumeSessionModal';
import { CallScreen } from './CallScreen';
import { ChatSessionScreen } from './ChatSessionScreen';
import { CallEndSummary } from './CallEndSummary';

/**
 * Global call overlay - renders call UI on top of everything.
 * Must be placed inside CallProvider.
 */
export function CallOverlay() {
  const {
    callState,
    callEndInfo,
    callError,
    callErrorSeverity,
    dismissError,
    phoneVerificationRequired,
    dismissPhoneVerification,
    activeCallType,
  } = useCall();
  const { user, refreshUser } = useAuth();

  return (
    <>
      {/* Active session screen: chat sessions reuse the call flow but render a
          message thread instead of the audio/video CallScreen. ChatSessionScreen
          only handles the ACTIVE state; non-active states (ringing/connecting/
          paused/ending) are handled by CallScreen, which renders the generic
          "in attesa"/cancel and pause/recharge UI for chat sessions too. Both
          internally guard their own visibility, so branching here is safe. */}
      {activeCallType === 'chat' && callState === 'active' ? <ChatSessionScreen /> : <CallScreen />}

      {/* Incoming call modal (for professionals) */}
      <IncomingCallModal />

      {/* Rientro in sessione viva dopo refresh/crash */}
      <ResumeSessionModal />

      {/* Call end summary with rating */}
      {callEndInfo && <CallEndSummary />}

      {/* Phone verification gate: opened when a call is blocked by PHONE_NOT_VERIFIED */}
      <Modal
        open={phoneVerificationRequired}
        onOpenChange={(open) => {
          if (!open) dismissPhoneVerification();
        }}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Verifica il tuo numero</ModalTitle>
            <ModalDescription>
              Per avviare una chiamata devi prima verificare il tuo numero di telefono.
            </ModalDescription>
          </ModalHeader>
          <div className="mt-4">
            <PhoneVerification
              initialPhone={user?.profile?.phone ?? ''}
              onVerified={async () => {
                await refreshUser();
                dismissPhoneVerification();
              }}
            />
          </div>
        </ModalContent>
      </Modal>

      {/* Error toast — also while an incoming call is ringing, so a device
          permission denied on "Rispondi" is visible above the modal */}
      {callError && (callState === 'idle' || callState === 'incoming') && (
        <div className="fixed bottom-6 left-1/2 z-[100] -translate-x-1/2 animate-in fade-in slide-in-from-bottom-4">
          <div
            className={`flex items-center gap-3 rounded-xl border bg-white px-5 py-3 shadow-lg ${
              callErrorSeverity === 'info' ? 'border-neutral-200' : 'border-red-200'
            }`}
          >
            {callErrorSeverity === 'info' ? (
              <svg className="h-5 w-5 flex-shrink-0 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
              </svg>
            ) : (
              <svg className="h-5 w-5 flex-shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            )}
            <span className="text-sm text-neutral-700">{callError}</span>
            <button
              onClick={dismissError}
              className="ml-2 text-neutral-400 hover:text-neutral-600"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
